import type { ThirdPartyResponseAction } from '@risksmart-app/domain/src/types/consts/third-party-response-action';
import type { UpdateThirdPartyResponseStatusMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateThirdPartyResponseStatusInput {
  Action: ThirdPartyResponseAction;
  ResponseIds: string[];
  Reason?: string;
  RequestType?: string;
  ShareWithRespondents: boolean;
  ThirdPartyId: string;
}

/**
 * Maps the tRPC mutation response to the GraphQL mutation response shape so
 * consumers branching on the result do not need to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): UpdateThirdPartyResponseStatusMutation => ({
  updateThirdPartyResponseStatusAction: {
    __typename: 'GenericMutationOutput',
    affected_rows: data.affected_rows,
  },
});

export const useUpdateThirdPartyResponseStatusTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.thirdPartyResponse.updateStatus.mutationOptions({
      onSuccess: async () => {
        // The reviewer screens read the response through `getById`; the
        // questionnaires tab lists them per third party. Both show Status.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.thirdPartyResponse.pathKey(),
        });
      },
    }),
  });

  return {
    updateStatus: async (
      input: UpdateThirdPartyResponseStatusInput
    ): Promise<UpdateThirdPartyResponseStatusMutation> => {
      const result = await mutation.mutateAsync(input);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
